import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import Expandable from '../Expandable/Expandable';
import Icon from '../generic/Icon';
import { WrapTrackingContext } from '../generic/TrackingContext';
import type { Facet, FacetGroup } from '../model/Facets';
import eventTargetValue from '../utils/eventTargetValue';
import preventDefault from '../utils/preventDefault';
import removeDiacritics from '../utils/removeDiacritics';
import useBooleanState from '../utils/useBooleanState';
import FacetValue from './FacetValue';
import * as style from './Facets.module.css';
import PriceRangeFacet from './PriceRangeFacet';
import type { Props as StoreFacetProps } from './StoreFacet';
import StoreFacet from './StoreFacet';

export interface Props extends StoreFacetProps {
    facets: FacetGroup[];
    onFacetValueSelect(query: string): Promise<void> | void;
}

export function Facets({ facets, onFacetValueSelect, onLocationChange }: Props) {
    return (
        <ul className={style.facets}>
            {facets.map((facetGroup) => (
                <li key={facetGroup.code} className={`facet-group__${facetGroup.code}`}>
                    <FacetGroup
                        key={facetGroup.code}
                        facetGroup={facetGroup}
                        onFacetValueSelect={onFacetValueSelect}
                        onLocationChange={onLocationChange}
                    />
                </li>
            ))}
        </ul>
    );
}

interface FacetGroupProps extends StoreFacetProps {
    facetGroup: FacetGroup;
    onFacetValueSelect(query: string): Promise<void> | void;
}

function FacetGroup({ facetGroup, onFacetValueSelect, onLocationChange }: FacetGroupProps) {
    return facetGroup.expanded ? (
        <FacetList facets={facetGroup.facets} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
    ) : (
        <WrapTrackingContext set={{ context: 'Filter' }}>
            <Expandable title={facetGroup.name}>
                <FacetList facets={facetGroup.facets} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
            </Expandable>
        </WrapTrackingContext>
    );
}

export interface FacetListProps extends StoreFacetProps {
    facets: Facet[];
    onFacetValueSelect(query: string): Promise<void> | void;
}

export function FacetList({ facets, onFacetValueSelect, onLocationChange }: FacetListProps) {
    if (facets.length === 1 && !facets[0].booleanFacet) {
        return (
            <WrapTrackingContext set={{ filter_type: facets[0].name }}>
                <FacetGroupItem facet={facets[0]} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
            </WrapTrackingContext>
        );
    }

    return (
        <>
            <ul className={style.facetGroup}>
                {facets.map((facet) => (
                    <WrapTrackingContext key={facet.code} set={{ filter_type: facet.name }}>
                        {facet.booleanFacet ? (
                            <li className={style.booleanFacet}>
                                <FacetValue value={facet.values[0]} onFacetValueSelect={onFacetValueSelect} />
                            </li>
                        ) : (
                            <li>
                                <div className={style.facetName}>{facet.name}</div>
                                <FacetGroupItem facet={facet} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
                            </li>
                        )}
                    </WrapTrackingContext>
                ))}
            </ul>
        </>
    );
}

function FacetGroupItem({ facet, onFacetValueSelect, onLocationChange }: FacetProps & StoreFacetProps) {
    if (facet.userInputtedInterval) {
        return <PriceRangeFacet facet={facet} onFacetValueSelect={onFacetValueSelect} />;
    }

    if (facet.code === 'availableInStores') {
        return (
            <>
                <StoreFacet onLocationChange={onLocationChange} />
                <FacetValues facet={facet} onFacetValueSelect={onFacetValueSelect} />
            </>
        );
    }

    if (facet.values.length >= 15) {
        return <FilterableFacetValues facet={facet} onFacetValueSelect={onFacetValueSelect} />;
    }

    return <FacetValues facet={facet} onFacetValueSelect={onFacetValueSelect} />;
}

function FilterableFacetValues({ facet, onFacetValueSelect }: FacetProps) {
    const [filter, setFilter] = useState('');
    const [sortByAlternativeMethod, setSortByAlternativeMethod] = useBooleanState();

    const onSelect = useCallback(
        async (query: string) => {
            await onFacetValueSelect(query);
            setFilter('');
        },
        [onFacetValueSelect],
    );

    const filteredValues = useMemo(() => {
        if (!filter) return facet.values;

        const filterNormalized = removeDiacritics(filter);
        return facet.values.filter((value) => value.normalizedName.includes(filterNormalized));
    }, [facet.values, filter]);

    const values = useMemo(
        () =>
            sortByAlternativeMethod
                ? facet.alternateSort === 'count'
                    ? filteredValues.toSorted((a, b) => b.count - a.count)
                    : filteredValues.toSorted((a, b) => a.name.localeCompare(b.name, 'nb-no'))
                : filteredValues,
        [filteredValues, sortByAlternativeMethod],
    );

    const onSubmit = useCallback(() => {
        if (values.length === 1) {
            onSelect(values[0].query);
        } else if (document.activeElement instanceof HTMLElement) {
            document.activeElement.closest('li')?.querySelector('a')?.focus();
        }
    }, [values, onSelect]);

    return (
        <>
            <form className={style.filterBox} onSubmit={preventDefault(onSubmit)}>
                <button className={style.filterIcon} type="submit" tabIndex={-1} aria-hidden="true">
                    <Icon className="icon-global-search" />
                </button>
                <input type="text" placeholder={`Søk etter ${facet.name.toLowerCase()}`} value={filter} onChange={eventTargetValue(setFilter)} />
                <button type="button" onClick={setSortByAlternativeMethod.toggle} title="Endre sortering" aria-label="Endre sortering">
                    <Icon className={style.iconSort} />
                </button>
            </form>
            <FacetValues facet={{ ...facet, values }} onFacetValueSelect={onSelect} />
        </>
    );
}

interface FacetProps {
    facet: Facet;
    onFacetValueSelect(query: string): Promise<void> | void;
}

function FacetValues({ facet, onFacetValueSelect }: FacetProps) {
    return (
        <ul className={cn(style.facetValues, facet.listClass)}>
            {facet.values.map((value) => (
                <li key={value.code}>
                    <FacetValue value={value} onFacetValueSelect={onFacetValueSelect} />
                </li>
            ))}
        </ul>
    );
}
